/**
 * @module mergeEngine
 *
 * Centralized row matching and merge logic for file upload workflows.
 *
 * Consolidates patterns previously duplicated across CRE's handleUpload,
 * blotter's mergeFileDataWithExistingOverrides, and Holdings/Surveillance
 * inline handlers.
 *
 * **Architecture role:** This is the final step in the upload pipeline, called
 * by `useFileUploadProcessor` after parsing, normalization, transformation, and
 * validation. It takes the processed uploaded rows and merges them with existing
 * grid data according to one of three strategies: `append`, `upsert`, or `replace`.
 *
 * **When to use directly:** You generally do NOT call `mergeRows` directly.
 * Instead, pass `mergeStrategy`, `matchField`, and other merge options to
 * `useUploadConfig` or `useFileUploadProcessor`, and they call `mergeRows`
 * internally. Direct use is only needed for custom merge logic outside the
 * standard upload pipeline.
 *
 * @example <caption>Direct usage (rare -- prefer useUploadConfig)</caption>
 * import { mergeRows } from '@1823-partners/core';
 *
 * const result = mergeRows({
 *   uploadedRows: parsedFileData,
 *   existingRows: currentGridData,
 *   matchField: 'CUSIP',
 *   mergeStrategy: 'upsert',
 *   deduplicateUpload: true,
 *   uploadableFields: ['BOOK_YIELD', 'WAL_CURR', 'PRICE'],
 *   onNewRow: (row, index) => ({ ...row, MODIFIED_BY: 'tyler.duic', IS_NEW: true }),
 * });
 *
 * setGridData(result.mergedData);
 * showNotification(`${result.newCount} new, ${result.updatedCount} updated`);
 */

/**
 * @typedef {Object} MergeResult
 * @property {Array<Object>} mergedData - The final merged row array. For `upsert`,
 *   new rows are prepended before existing rows (in their original order, with
 *   updates applied in-place). For `append` and `replace`, only uploaded rows
 *   are included.
 * @property {number} newCount - Number of uploaded rows that did not match any
 *   existing row (new additions).
 * @property {number} updatedCount - Number of existing rows that were updated by
 *   a matching uploaded row.
 * @property {number} unchangedCount - Number of uploaded rows that matched an
 *   existing row but had no field changes (upsert strategy only).
 * @property {Array<string>} updatedKeys - Array of `matchField` values for rows
 *   that were updated. Useful for highlighting changed rows in the grid.
 * @property {Object<string, Object<string, {oldValue: *, newValue: *}>>} fieldChanges -
 *   Nested object tracking what changed. Outer key is the `matchField` value,
 *   inner key is the field name, value is `{ oldValue, newValue }`. Only
 *   populated when using the default merge (not `onUpdateRow`).
 *   Example: `{ '14913UAN0': { BOOK_YIELD: { oldValue: 3.5, newValue: 4.0 } } }`
 * @property {Array<string>} duplicatesFound - Array of `matchField` values that
 *   appeared more than once in the uploaded file (only when `deduplicateUpload=true`).
 * @property {number} duplicateCount - Total number of duplicate rows removed
 *   from the uploaded data before merging.
 */

/**
 * @typedef {Object} FieldChange
 * @property {*} oldValue - The value in the existing row before the merge
 * @property {*} newValue - The value from the uploaded row that replaced it
 */

/**
 * Returns true when a value is considered "empty" for merge purposes.
 * Empty values in uploaded rows do NOT overwrite existing values during upsert.
 *
 * @param {*} value
 * @returns {boolean} True if value is `null`, `undefined`, or `''`
 * @private
 */
function isEmpty(value) {
  return value === null || value === undefined || value === '';
}

/**
 * Deduplicate rows by a key field within the uploaded file.
 *
 * When a file contains multiple rows with the same key (e.g., two rows for
 * CUSIP `14913UAN0`), this function keeps the first occurrence and gap-fills
 * it with non-null values from subsequent duplicates. "Gap-fill" means: if the
 * first row has a null/empty cell but a later duplicate has a value for that
 * field, the value is copied into the first row.
 *
 * **First-occurrence-wins for conflicts:** If both the first and a later row
 * have non-empty values for the same field, the first row's value is kept.
 *
 * **When to use:** Enable via `deduplicateUpload: true` in your merge config.
 * This is useful for blotter Overrides where users may paste the same CUSIP
 * from multiple tabs in their spreadsheet.
 *
 * @param {Array<Object>} rows - Array of row objects to deduplicate
 * @param {string} keyField - Field name to deduplicate on (e.g., `'CUSIP'`)
 * @returns {{ deduped: Array<Object>, duplicates: Array<string> }}
 *   - `deduped`: Deduplicated rows in original order (first occurrence of each key)
 *   - `duplicates`: Array of key values that had duplicates (for reporting)
 *
 * @example
 * import { deduplicateRows } from '@1823-partners/core';
 *
 * const rows = [
 *   { CUSIP: '14913UAN0', YIELD: 3.5, PRICE: null },
 *   { CUSIP: '14913UAN0', YIELD: 4.0, PRICE: 99.5 },  // duplicate
 *   { CUSIP: '31394ABC1', YIELD: 2.0, PRICE: 100 },
 * ];
 *
 * const { deduped, duplicates } = deduplicateRows(rows, 'CUSIP');
 * // deduped = [
 * //   { CUSIP: '14913UAN0', YIELD: 3.5, PRICE: 99.5 },  // YIELD from first, PRICE gap-filled
 * //   { CUSIP: '31394ABC1', YIELD: 2.0, PRICE: 100 },
 * // ]
 * // duplicates = ['14913UAN0']
 */
export function deduplicateRows(rows, keyField) {
  const seen = new Map();
  const duplicates = [];

  for (const row of rows) {
    const key = row[keyField];

    if (!seen.has(key)) {
      // First occurrence -- clone to avoid mutating the original
      seen.set(key, { ...row });
    } else {
      // Track duplicate key (only add once per key)
      if (!duplicates.includes(key)) {
        duplicates.push(key);
      }
      // Gap-fill: copy non-empty fields from this duplicate into the base row
      const base = seen.get(key);
      for (const field of Object.keys(row)) {
        if (isEmpty(base[field]) && !isEmpty(row[field])) {
          base[field] = row[field];
        }
      }
    }
  }

  // Preserve original order of first occurrences
  const deduped = [];
  for (const row of rows) {
    const key = row[keyField];
    const stored = seen.get(key);
    if (stored !== undefined) {
      deduped.push(stored);
      // Delete so we only push it once
      seen.delete(key);
    }
  }

  return { deduped, duplicates };
}

/**
 * Default upsert merge: for each field in the uploaded row, overwrite the
 * existing value only when the uploaded value is non-empty and different.
 * Empty uploaded values preserve the existing value.
 *
 * This is the merge function used when `onUpdateRow` is NOT provided. It
 * also computes per-field change tracking (returned as `changes`).
 *
 * @param {Object} existingRow - The current row from the grid
 * @param {Object} uploadedRow - The uploaded row to merge from
 * @param {Array<string>|undefined} uploadableFields - If provided, only these
 *   fields are eligible for update. Fields not in this list are ignored even
 *   if the uploaded row has values for them. This prevents accidental overwrites
 *   of calculated or system fields (like MODIFIED_DATE).
 * @returns {{ merged: Object, changes: Object<string, FieldChange> }}
 *   - `merged`: The merged row object (clone of existing with updates applied)
 *   - `changes`: Map of field name to `{ oldValue, newValue }` for fields that changed
 * @private
 */
function defaultMerge(existingRow, uploadedRow, uploadableFields) {
  const merged = { ...existingRow };
  const changes = {};

  const fieldsToCheck =
    uploadableFields != null ? uploadableFields : Object.keys(uploadedRow);

  for (const field of fieldsToCheck) {
    if (!(field in uploadedRow)) {
      continue;
    }
    const uploadedValue = uploadedRow[field];
    if (isEmpty(uploadedValue)) {
      // Keep existing
      continue;
    }
    const existingValue = existingRow[field];
    if (uploadedValue !== existingValue) {
      merged[field] = uploadedValue;
      changes[field] = { oldValue: existingValue, newValue: uploadedValue };
    }
  }

  return { merged, changes };
}

/**
 * Merge uploaded rows with existing grid data.
 *
 * This is the core merge function used by the upload pipeline. It supports
 * three merge strategies:
 *
 * ### Strategy: `'append'`
 * All uploaded rows are treated as new. Existing rows are ignored. The
 * `onNewRow` callback is called for each row. Good for entry lists where
 * every upload creates new records.
 *
 * ```
 * Uploaded: [A, B, C]    Existing: [X, Y]    Result: [A, B, C]
 * ```
 *
 * ### Strategy: `'replace'`
 * All uploaded rows replace existing data entirely. Like `append`, but
 * semantically indicates the old data is being replaced.
 *
 * ```
 * Uploaded: [A, B, C]    Existing: [X, Y]    Result: [A, B, C]
 * ```
 *
 * ### Strategy: `'upsert'` (default)
 * Each uploaded row is matched against existing rows by `matchField`.
 * - **Match found:** The existing row is updated (non-empty uploaded values
 *   overwrite existing values; empty uploaded values are skipped).
 * - **No match:** The uploaded row is treated as new and prepended.
 * - New rows appear BEFORE existing rows in the result.
 *
 * ```
 * Uploaded: [{CUSIP:'A', YIELD:5}, {CUSIP:'C', YIELD:3}]
 * Existing: [{CUSIP:'A', YIELD:4, PRICE:100}, {CUSIP:'B', YIELD:2}]
 * Result:   [{CUSIP:'C', YIELD:3},                    // new (prepended)
 *            {CUSIP:'A', YIELD:5, PRICE:100},          // updated (YIELD changed)
 *            {CUSIP:'B', YIELD:2}]                     // unchanged
 * ```
 *
 * @param {Object} config - Merge configuration
 * @param {Array<Object>} config.uploadedRows - Parsed and transformed rows from
 *   the uploaded file.
 * @param {Array<Object>} config.existingRows - Current grid data (the rows
 *   currently displayed in the AG Grid).
 * @param {string} config.matchField - Field name to match uploaded rows against
 *   existing rows. Typically `'CUSIP'` for security-level data. The match is
 *   exact (===) on the field value.
 * @param {'append'|'upsert'|'replace'} [config.mergeStrategy='upsert'] - Which
 *   merge strategy to use. See strategy descriptions above.
 * @param {boolean} [config.deduplicateUpload=false] - When true, deduplicate the
 *   uploaded rows by `matchField` before merging. Uses `deduplicateRows()` which
 *   keeps the first occurrence and gap-fills from later duplicates.
 * @param {Function} [config.onNewRow] - Callback for new rows (no match found).
 *   Signature: `(row, index) => enrichedRow`. Use this to add default values,
 *   timestamps, or user info to new rows before they enter the grid.
 *   Example: `(row, i) => ({ ...row, MODIFIED_BY: userId, MODIFIED_DATE: today })`
 * @param {Function} [config.onUpdateRow] - Callback for matched rows. Replaces
 *   the default merge logic entirely. Signature:
 *   `(existingRow, uploadedRow) => mergedRow`. When provided, `fieldChanges` is
 *   NOT computed (the caller owns change tracking). Use this when you need custom
 *   merge rules (e.g., only update if the uploaded value is higher).
 * @param {Array<string>} [config.uploadableFields] - Restrict which fields from
 *   the uploaded row can overwrite existing values during upsert. Fields not in
 *   this list are ignored. Use this to protect system fields (`MODIFIED_DATE`,
 *   `CREATED_BY`, etc.) from being overwritten by spreadsheet data.
 * @returns {MergeResult} The merge result containing `mergedData` and statistics.
 *   See the `MergeResult` typedef for full documentation of each field.
 *
 * @example <caption>Simple append (blotter EntryList)</caption>
 * import { mergeRows } from '@1823-partners/core';
 *
 * const result = mergeRows({
 *   uploadedRows: parsedData,
 *   existingRows: [],          // no existing data for append
 *   matchField: 'CUSIP',       // required but not used for append
 *   mergeStrategy: 'append',
 *   onNewRow: (row, i) => ({
 *     ...row,
 *     MODIFIED_BY: 'tyler.duic',
 *     MODIFIED_DATE: '2026-04-01',
 *     ROW_ID: `new-${i}`,
 *   }),
 * });
 * // result.mergedData = [...all uploaded rows with enrichment]
 * // result.newCount = parsedData.length
 *
 * @example <caption>CUSIP-based upsert (CRE loans)</caption>
 * const result = mergeRows({
 *   uploadedRows: parsedData,
 *   existingRows: currentGridData,
 *   matchField: 'CUSIP',
 *   mergeStrategy: 'upsert',
 *   uploadableFields: ['LOAN_NAME', 'PROPERTY_NAME', 'YIELD', 'MATURITY_DATE'],
 *   onNewRow: (row) => ({ ...row, MODIFIED_BY: userId }),
 * });
 * // result.updatedKeys = ['14913UAN0', '31394ABC1'] -- CUSIPs that were updated
 * // result.fieldChanges = { '14913UAN0': { YIELD: { oldValue: 3.5, newValue: 4.0 } } }
 *
 * @example <caption>Upsert with deduplication (blotter Overrides)</caption>
 * const result = mergeRows({
 *   uploadedRows: parsedData,
 *   existingRows: overrideData,
 *   matchField: 'CUSIP',
 *   mergeStrategy: 'upsert',
 *   deduplicateUpload: true,
 *   onNewRow: (row) => ({ ...row, MODIFIED_BY: userId }),
 *   onUpdateRow: (existing, uploaded) => ({
 *     ...existing,
 *     ...uploaded,
 *     MODIFIED_BY: userId,
 *     MODIFIED_DATE: today,
 *   }),
 * });
 * // result.duplicatesFound = ['14913UAN0'] -- CUSIPs that appeared twice in file
 * // result.duplicateCount = 1
 */
export function mergeRows(config) {
  const {
    uploadedRows,
    existingRows,
    matchField,
    mergeStrategy = 'upsert',
    deduplicateUpload = false,
    onNewRow,
    onUpdateRow,
    uploadableFields,
  } = config;

  const emptyResult = {
    mergedData: [],
    newCount: 0,
    updatedCount: 0,
    unchangedCount: 0,
    updatedKeys: [],
    fieldChanges: {},
    duplicatesFound: [],
    duplicateCount: 0,
  };

  // -------------------------------------------------------------------------
  // append strategy: all uploaded rows are new
  // -------------------------------------------------------------------------
  if (mergeStrategy === 'append') {
    const newRows = uploadedRows.map((row, i) =>
      onNewRow ? onNewRow(row, i) : row
    );
    return {
      ...emptyResult,
      mergedData: newRows,
      newCount: newRows.length,
    };
  }

  // -------------------------------------------------------------------------
  // replace strategy: uploaded rows replace all existing data
  // -------------------------------------------------------------------------
  if (mergeStrategy === 'replace') {
    const newRows = uploadedRows.map((row, i) =>
      onNewRow ? onNewRow(row, i) : row
    );
    return {
      ...emptyResult,
      mergedData: newRows,
      newCount: newRows.length,
    };
  }

  // -------------------------------------------------------------------------
  // upsert strategy
  // -------------------------------------------------------------------------
  let rowsToProcess = uploadedRows;
  let duplicatesFound = [];
  let duplicateCount = 0;

  if (deduplicateUpload) {
    const { deduped, duplicates } = deduplicateRows(uploadedRows, matchField);
    rowsToProcess = deduped;
    duplicatesFound = duplicates;
    duplicateCount = uploadedRows.length - deduped.length;
  }

  // Build a mutable map of existing rows keyed by matchField value.
  // We use an array of [key, row] entries to preserve order.
  const existingMap = new Map();
  for (const row of existingRows) {
    const key = row[matchField];
    existingMap.set(key, { ...row });
  }

  const newRows = [];
  let newCount = 0;
  let updatedCount = 0;
  let unchangedCount = 0;
  const updatedKeys = [];
  const fieldChanges = {};

  rowsToProcess.forEach((uploadedRow, i) => {
    const key = uploadedRow[matchField];

    if (existingMap.has(key)) {
      // Matched -> UPDATE
      const existingRow = existingMap.get(key);

      if (onUpdateRow) {
        const merged = onUpdateRow(existingRow, uploadedRow);
        existingMap.set(key, merged);
        // We don't compute fieldChanges for custom onUpdateRow -- caller owns that logic
        updatedCount++;
        updatedKeys.push(key);
      } else {
        const { merged, changes } = defaultMerge(
          existingRow,
          uploadedRow,
          uploadableFields
        );

        if (Object.keys(changes).length > 0) {
          existingMap.set(key, merged);
          fieldChanges[key] = changes;
          updatedCount++;
          updatedKeys.push(key);
        } else {
          unchangedCount++;
        }
      }
    } else {
      // No match -> NEW ROW
      const newRow = onNewRow ? onNewRow(uploadedRow, i) : uploadedRow;
      newRows.push(newRow);
      newCount++;
    }
  });

  // New rows prepended, then existing (in original order)
  const updatedExisting = existingRows.map((row) => {
    const key = row[matchField];
    return existingMap.has(key) ? existingMap.get(key) : row;
  });

  return {
    mergedData: [...newRows, ...updatedExisting],
    newCount,
    updatedCount,
    unchangedCount,
    updatedKeys,
    fieldChanges,
    duplicatesFound,
    duplicateCount,
  };
}
